import { token } from '@/token';
import type {
  ChangeEvent,
  EventsAuthMessage,
  EventsServerMessage,
  Kind,
} from './client';
import { BASE_URL } from './fetcher';

// Live changes from designs-backend's /events (see
// backend/designs-backend/designs_backend/server/events.py for the protocol).
// One socket per page, opened by useBackendSession once the session is set
// up. Every message after `ready` is a ChangeEvent: what kind of thing
// changed, how (created, updated, deleted), its id, who did it and where it
// belongs (e.g. a comment's page). It doesn't carry the thing itself, so a
// component that needs the new state fetches it (e.g. getComment(change.id)).
//
// The message types are generated; the path, the auth handshake and the
// close codes below are not. They mirror the backend by hand, and nothing
// fails if the two drift: see "Keeping the backend and the designs app in
// step" in backend/designs-backend/README.md.

type Listener = (change: ChangeEvent) => void;

const listeners = new Map<Kind, Set<Listener>>();

// Calls listener with every change to `kind` from now on, from anyone, on
// any page; filter on change.scope for a page's own. Returns the unsubscribe.
export const onChange = (kind: Kind, listener: Listener): (() => void) => {
  const forKind = listeners.get(kind) ?? new Set<Listener>();
  listeners.set(kind, forKind);
  forKind.add(listener);
  return () => {
    forKind.delete(listener);
  };
};

// Waits before reconnecting after an unexpected close; the last repeats.
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];
// Closes that are part of normal life, not failures: a replica going away
// for a deploy or scale-down, and the token running out. Reconnect at once,
// spread over a second so a whole deploy's clients don't arrive together.
const SERVICE_RESTART = 1012;
const GOING_AWAY = 1001;
const TOKEN_EXPIRED = 4401;

const streamUrl = () => {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}${BASE_URL}/events`;
};

let started = false;

export const startEventStream = () => {
  if (started) return;
  started = true;
  let failures = 0;

  const connect = () => {
    const socket = new WebSocket(streamUrl());
    let ready = false;

    socket.onopen = () => {
      token
        .getToken()
        .then((accessToken) => {
          if (!accessToken) throw new Error('no Kinde access token');
          const auth: EventsAuthMessage = { type: 'auth', token: accessToken };
          socket.send(JSON.stringify(auth));
        })
        .catch((error: unknown) => {
          console.error('designs-backend event stream auth failed', error);
          socket.close();
        });
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      let message: EventsServerMessage;
      try {
        message = JSON.parse(event.data) as EventsServerMessage;
      } catch (error) {
        console.error('designs-backend event stream sent non-JSON', error);
        return;
      }
      if (message.type === 'ready') {
        ready = true;
        failures = 0;
        console.info('designs-backend event stream open');
        return;
      }
      console.debug('designs-backend change', message);
      // Kinds this bundle doesn't know (added since it loaded) have no
      // listeners, so they're dropped.
      listeners.get(message.kind)?.forEach((listener) => listener(message));
    };

    socket.onclose = (event: CloseEvent) => {
      const routine =
        event.code === SERVICE_RESTART ||
        event.code === GOING_AWAY ||
        (ready && event.code === TOKEN_EXPIRED);
      const delay = routine
        ? Math.random() * 1_000
        : BACKOFF_MS[Math.min(failures++, BACKOFF_MS.length - 1)];
      const detail = {
        code: event.code,
        reason: event.reason,
        retryInMs: Math.round(delay),
      };
      if (routine) {
        // Expected; reconnecting is all there is to it.
      } else if (ready) {
        // A working stream dropped without a close frame (1006): nginx
        // draining for a deploy, or the network. Worth knowing, not an error.
        console.warn('designs-backend event stream dropped', detail);
      } else {
        console.error('designs-backend event stream failed', detail);
      }
      window.setTimeout(connect, delay);
    };
  };

  connect();
};
